import { useEffect, useRef, useState, FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeftIcon, EyeCloseIcon, EyeIcon } from "../../icons";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Checkbox from "../form/input/Checkbox";
import Button from "../ui/button/Button";
import GlobalLoader from "../common/GlobalLoader";
import {
  GoogleAuthProvider,
  OAuthProvider,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  signInWithCustomToken,
} from "firebase/auth";
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from "../../firebase/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { useFamilyMode } from "../../context/FamilyModeContext";
import { useToast } from "../ui/toast/ToastProvider";
import { useTranslation } from "react-i18next";
import { Capacitor } from '@capacitor/core';
import { useGoogleAuth } from "../../hooks/useGoogleAuth";
import { checkUserHasFamily } from "../../utils/authCheck";


// Helper: Retry logic para Firestore (optimizado para Android)
async function getUserDocWithRetry(uid: string, maxRetries = 5): Promise<any> {
  const isNative = Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios';
  const isAppleSignInSupported = Capacitor.getPlatform() === 'ios' || !isNative;
  // Android necesita más tiempo para establecer conexión con Firestore
  const baseDelay = isNative ? 500 : 300;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const userDocRef = doc(db, "users", uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        
        // En el primer intento, aceptar documento incluso sin familyIds
        // En siguientes intentos, esperar que se sincronice
        if (attempt === 0) {
          return { exists: true, data: userData };
        }
        
        // Si ya tiene familyIds, retornar inmediatamente
        if (userData?.familyId || (Array.isArray(userData?.familyIds) && userData.familyIds.length > 0)) {
          return { exists: true, data: userData };
        }
      }
    } catch (error: any) {
      // En caso de error de red, continuar con retry
      console.warn(`[SignInForm] Network error on attempt ${attempt + 1}:`, error?.code || error?.message);
      
      // Si es un error permanente (no de red), lanzar inmediatamente
      if (error?.code && !error.code.includes('unavailable') && !error.code.includes('network')) {
        throw error;
      }
    }
    
    // Si no es el último intento, esperar antes de reintentar
    if (attempt < maxRetries - 1) {
      const delay = baseDelay * Math.pow(2, attempt); // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // Último intento: retornar lo que haya
  try {
    const userDocRef = doc(db, "users", uid);
    const userDocSnap = await getDoc(userDocRef);
    return { 
      exists: userDocSnap.exists(), 
      data: userDocSnap.exists() ? userDocSnap.data() : null 
    };
  } catch (error) {
    console.error('[SignInForm] Final attempt failed:', error);
    // Retornar que no existe en lugar de lanzar error
    return { exists: false, data: null };
  }
}

export default function SignInForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { disable } = useFamilyMode();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Inicializar Google Auth para móvil
  const { isReady: googleAuthReady } = useGoogleAuth();
  const googleAuthReadyRef = useRef(googleAuthReady);
  useEffect(() => {
    googleAuthReadyRef.current = googleAuthReady;
  }, [googleAuthReady]);

  const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
    let tId: any;
    const timeoutPromise = new Promise<T>((_, reject) => {
      tId = setTimeout(() => reject(new Error(`Timeout: ${label}`)), ms);
    });
    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      clearTimeout(tId);
    }
  };

  const waitForGoogleAuthReady = async (ms: number) => {
    const end = Date.now() + ms;
    while (Date.now() < end) {
      if (googleAuthReadyRef.current) return;
      await new Promise((r) => setTimeout(r, 150));
    }
  };

  const signInWithGoogleOnly = async (): Promise<string> => {
    const platform = Capacitor.getPlatform();
    const isNative = platform === 'android' || platform === 'ios';

    if (isNative) {
      if (platform === 'ios') {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        const result = await withTimeout(
          FirebaseAuthentication.signInWithGoogle(),
          30000,
          'FirebaseAuthentication.signInWithGoogle'
        );
        const idToken = String(result?.credential?.idToken || '').trim();
        if (!idToken) {
          throw new Error('No Google ID token returned from FirebaseAuthentication.signInWithGoogle');
        }
        const credential = GoogleAuthProvider.credential(idToken);
        const cred = await withTimeout(
          signInWithCredential(auth, credential),
          30000,
          'signInWithCredential'
        );
        return cred.user.uid;
      }

      if (!googleAuthReadyRef.current) {
        await waitForGoogleAuthReady(4000);
      }
      if (!googleAuthReadyRef.current) {
        throw new Error(t('common.googleauthpluginnoestainicializadoesperaunmomentoyvuelveaintentar'));
      }

      const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
      try {
        await GoogleAuth.signOut();
      } catch {
      }
      const googleUser = await withTimeout(GoogleAuth.signIn(), 30000, 'GoogleAuth.signIn');
      if (!googleUser?.authentication?.idToken) {
        throw new Error('No Google ID token returned from GoogleAuth.signIn');
      }
      const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
      const cred = await withTimeout(signInWithCredential(auth, credential), 30000, 'signInWithCredential');
      return cred.user.uid;
    }

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const cred = await signInWithPopup(auth, provider);
    return cred.user.uid;
  };

  const handleAppleSignIn = async () => {
    setError(null);
    setLoading(true);

    const platform = Capacitor.getPlatform();
    const isNative = platform === 'android' || platform === 'ios';

    try {
      let uid: string;

      if (isNative) {
        if (platform !== 'ios') {
          throw new Error('Apple Sign-In is only supported on iOS in this build.');
        }

        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        const result = await withTimeout(
          FirebaseAuthentication.signInWithApple({ scopes: ['email', 'name'] }),
          120000,
          'FirebaseAuthentication.signInWithApple'
        );

        // Native sign-in is handled by the plugin. To access Firestore via Firebase JS SDK,
        // we hydrate the JS session using a custom token.
        uid = String(result?.user?.uid || '').trim();
        if (!uid) {
          const current = await FirebaseAuthentication.getCurrentUser();
          uid = String(current?.user?.uid || '').trim();
        }
        if (!uid) {
          throw new Error('No native user returned from FirebaseAuthentication.signInWithApple');
        }

        // Give Firebase a moment to finalize native sign-in before fetching a token.
        await new Promise((r) => setTimeout(r, 600));

        const tokenRes = await withTimeout(
          FirebaseAuthentication.getIdToken({ forceRefresh: true }),
          30000,
          'FirebaseAuthentication.getIdToken'
        );
        const nativeIdToken = String((tokenRes as any)?.token || '').trim();
        if (!nativeIdToken) {
          throw new Error('No ID token returned from FirebaseAuthentication.getIdToken');
        }

        const exchange = httpsCallable(functions, 'exchangeIdTokenForCustomToken');
        let resp: any;
        try {
          resp = await withTimeout(
            exchange({ idToken: nativeIdToken }),
            30000,
            'exchangeIdTokenForCustomToken'
          );
        } catch (e: any) {
          const msg = String(e?.message || '');
          // Retry once with a forced refresh in case the first token is stale.
          if (msg.includes('unauthenticated') || msg.includes('Invalid idToken')) {
            const tokenRes2 = await withTimeout(
              FirebaseAuthentication.getIdToken({ forceRefresh: true }),
              30000,
              'FirebaseAuthentication.getIdToken (retry)'
            );
            const nativeIdToken2 = String((tokenRes2 as any)?.token || '').trim();
            if (!nativeIdToken2) {
              throw e;
            }
            resp = await withTimeout(
              exchange({ idToken: nativeIdToken2 }),
              30000,
              'exchangeIdTokenForCustomToken (retry)'
            );
          } else {
            throw e;
          }
        }
        const customToken = String(resp?.data?.customToken || '').trim();
        if (!customToken) {
          throw new Error('No custom token returned from exchangeIdTokenForCustomToken');
        }

        const jsCred = await withTimeout(
          signInWithCustomToken(auth, customToken),
          30000,
          'signInWithCustomToken'
        );
        uid = jsCred.user.uid;
      } else {
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        const cred = await signInWithPopup(auth, provider);
        uid = cred.user.uid;
      }

      // Esperar a que onAuthStateChanged se dispare
      await new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          if (user && (!uid || user.uid === uid)) {
            unsubscribe();
            resolve(null);
          }
        });
        const timeout = isNative ? 10000 : 3000;
        setTimeout(() => {
          unsubscribe();
          resolve(null);
        }, timeout);
      });

      const result = await withTimeout(getUserDocWithRetry(uid), 30000, 'getUserDocWithRetry');
      if (!result.exists) {
        await auth.signOut();
        setLoading(false);
        setTimeout(() => {
          navigate('/signup?from=signin&reason=user-not-found', {
            replace: true,
            state: { notice: t('auth.userNotFoundMessage', { defaultValue: 'No encontramos tu cuenta. Crea una nueva para continuar.' }) },
          });
        }, 100);
        return;
      }

      const userData = result.data;
      const hasFamilies = checkUserHasFamily(userData);
      setLoading(false);
      if (!hasFamilies) {
        sessionStorage.setItem('redirecting_to_onboarding', 'true');
        navigate('/onboarding/welcome', { replace: true });
        return;
      }

      setTimeout(() => {
        navigate('/', { replace: true });
      }, 100);
    } catch (e: any) {
      if (
        e?.code === 'auth/popup-closed-by-user' ||
        e?.code === 'auth/cancelled-popup-request' ||
        e?.message?.includes('popup') ||
        e?.message?.includes('cancelled')
      ) {
        setError(t('common.usuariocancelóellogin', { defaultValue: 'El usuario canceló el inicio de sesión' }));
      } else {
        const msg = e?.message || t('auth.appleSignInFailed', { defaultValue: 'No se pudo iniciar sesión con Apple' });
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    
    try {
      const platform = Capacitor.getPlatform();
      const isNative = platform === 'android' || platform === 'ios';
      let uid: string;

      const startTs = Date.now();
      const step = (msg: string, extra?: any) => {
        const ms = Date.now() - startTs;
        if (extra !== undefined) {
          console.log(`[SignInForm][Google][+${ms}ms] ${msg}`, extra);
        } else {
          console.log(`[SignInForm][Google][+${ms}ms] ${msg}`);
        }
      };
      step('start', { isNative });

      if (isNative) {
        // Android/iOS

        if (platform === 'ios') {
          const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');

          step('calling FirebaseAuthentication.signInWithGoogle');
          const result = await withTimeout(
            FirebaseAuthentication.signInWithGoogle(),
            30000,
            'FirebaseAuthentication.signInWithGoogle'
          );
          const idToken = String(result?.credential?.idToken || '').trim();
          step('FirebaseAuthentication.signInWithGoogle returned', { hasIdToken: Boolean(idToken) });

          if (!idToken) {
            throw new Error('No Google ID token returned from FirebaseAuthentication.signInWithGoogle');
          }

          const credential = GoogleAuthProvider.credential(idToken);
          step('calling signInWithCredential');
          const cred = await withTimeout(
            signInWithCredential(auth, credential),
            30000,
            'signInWithCredential'
          );
          step('signInWithCredential ok', { uid: cred.user.uid, email: cred.user.email });
          uid = cred.user.uid;
        } else {
          if (!googleAuthReadyRef.current) {
            await waitForGoogleAuthReady(4000);
          }
          if (!googleAuthReadyRef.current) {
            throw new Error(t('common.googleauthpluginnoestainicializadoesperaunmomentoyvuelveaintentar'));
          }

          const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');

          // Forzar selector de cuenta (evita que reutilice la última cuenta elegida)
          try {
            await GoogleAuth.signOut();
          } catch {
            // ignore
          }

          step('calling GoogleAuth.signIn');
          let googleUser: any = null;
          try {
            googleUser = await withTimeout(GoogleAuth.signIn(), 30000, 'GoogleAuth.signIn');
          } catch (err: any) {
            // Retry 1 vez si el plugin queda colgado / no abre selector a tiempo
            const msg = String(err?.message || '');
            if (msg.includes('Timeout: GoogleAuth.signIn')) {
              step('GoogleAuth.signIn timeout; retrying once');
              try {
                await GoogleAuth.signOut();
              } catch {
                // ignore
              }
              await new Promise((r) => setTimeout(r, 250));
              googleUser = await withTimeout(GoogleAuth.signIn(), 30000, 'GoogleAuth.signIn (retry)');
            } else {
              throw err;
            }
          }
          step('GoogleAuth.signIn returned', {
            hasIdToken: Boolean(googleUser?.authentication?.idToken),
            email: googleUser?.email,
          });

          if (!googleUser?.authentication?.idToken) {
            throw new Error('No Google ID token returned from GoogleAuth.signIn');
          }

          const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);

          step('calling signInWithCredential');
          const cred = await withTimeout(
            signInWithCredential(auth, credential),
            30000,
            'signInWithCredential'
          );
          step('signInWithCredential ok', { uid: cred.user.uid, email: cred.user.email });
          uid = cred.user.uid;
        }
      } else {
        // Web: Usar popup
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: "select_account" });
        const cred = await signInWithPopup(auth, provider);
        uid = cred.user.uid;
      }

      // Esperar a que onAuthStateChanged se dispare
      step('waiting onAuthStateChanged');
      await new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          if (user && (!uid || user.uid === uid)) {
            unsubscribe();
            resolve(null);
          }
        });
        // Timeout más largo para Android (necesita tiempo para establecer conexión)
        const isNative = Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios';
        const timeout = isNative ? 10000 : 3000;
        setTimeout(() => {
          unsubscribe();
          resolve(null);
        }, timeout);
      });
      step('onAuthStateChanged wait done');

      let userDoc = null;
      try {
        // Usar retry logic para asegurar sincronización de Firestore
        step('calling getUserDocWithRetry');
        const result = await withTimeout(getUserDocWithRetry(uid), 30000, 'getUserDocWithRetry');
        step('getUserDocWithRetry done', { exists: result?.exists, hasData: Boolean(result?.data) });

        if (!result.exists) {
          await auth.signOut();
          setLoading(false); // Desactivar loading ANTES de navigate
          setTimeout(() => {
            navigate("/signup?from=signin&reason=user-not-found", {
              replace: true,
              state: { notice: t("auth.userNotFoundMessage", { defaultValue: "No encontramos tu cuenta. Crea una nueva para continuar." }) },
            });
          }, 100);
          return;
        }

        // Verificar si el usuario necesita onboarding
        const userData = result.data;
        userDoc = userData; // Guardar para usar en Family Mode check
        const hasFamilies = checkUserHasFamily(userData);
        
        if (!hasFamilies) {
          let skipped = false;
          let dismissed = false;
          try {
            skipped = localStorage.getItem('onboarding_skipped') === '1';
            dismissed = localStorage.getItem('onboarding_prompt_dismissed') === '1';
          } catch {
          }

          // Si el usuario eligió saltar onboarding (y/o ya descartó el modal),
          // no forzar onboarding aquí. Dejar que HomeRedirect/Dashboard manejen el flujo.
          if (skipped) {
            setLoading(false);
            navigate("/", { replace: true });
            return;
          }

          setLoading(false);
          sessionStorage.setItem("redirecting_to_onboarding", "true");
          navigate("/onboarding/welcome", { replace: true });
          return;
        }

              } catch (err) {
        console.error(t('common.errorcheckinguser'), err);
        step('errorcheckinguser', err);
      }

      // Family Mode exit flow
      const params = new URLSearchParams(location.search);
      const exitFamilyMode = params.get("exitFamilyMode") === "1";

      if (exitFamilyMode && userDoc) {
        const userData = userDoc;
        const role = userData?.role;
        const isOwner = userData?.isOwner === true || role === "owner";
        const isAdmin =
          userData?.isAdmin === true ||
          role === "admin" ||
          role === "superadmin";

        if (isOwner || isAdmin) {
          disable();
          showToast({ type: "success", message: t("auth.familyModeDisabled") });
          setLoading(false); // Desactivar loading ANTES de navigate
          setTimeout(() => navigate("/groups/dashboard", { replace: true }), 100);
          return;
        } else {
          showToast({
            type: "error",
            message: t("auth.adminRequiredToExitFamilyMode"),
          });
          setLoading(false); // Desactivar loading ANTES de navigate
          setTimeout(() => navigate("/groups/home-groups", { replace: true }), 100);
          return;
        }
      }

      // Navegar al dashboard después de confirmar autenticación
      setLoading(false); // Desactivar loading ANTES de navigate
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 100);
    } catch (e: any) {
      console.error(t('common.erroringenoglesignin'), e);
      console.error(t('common.errorcode'), e?.code);
      console.error(t('common.errormessage'), e?.message);
      console.error(t('common.fullerror'), JSON.stringify(e, null, 2));
      
      if (
        e?.code === "auth/popup-closed-by-user" ||
        e?.code === "auth/cancelled-popup-request" ||
        e?.message?.includes('popup') ||
        e?.message?.includes('cancelled')
      ) {
        setError(t('common.usuariocancelóellogin', { defaultValue: 'El usuario canceló el inicio de sesión' }));
      } else if (
        e?.code === 'unavailable' ||
        e?.code?.includes('network') ||
        e?.message?.toLowerCase().includes('quic') ||
        e?.message?.toLowerCase().includes('network')
      ) {
        // Error de red/QUIC - dar mensaje más específico
        setError(t('common.errordeconexiónporfavorverificatuyinternetintentadevuelta'));
        console.error("[SignInForm] Error de red detectado en Android");
      } else {
        const errorMsg = e?.message || t("auth.googleSignInFailed");
        setError(errorMsg);
        console.error("[SignInForm] Mostrando error al usuario:", errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError(t("auth.completeEmailAndPassword"));
      return;
    }

    setLoading(true);

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await new Promise((resolve) => setTimeout(resolve, 500));

      let userDoc = null;
      try {
        // Usar retry logic específico para Android
        const result = await getUserDocWithRetry(cred.user.uid);

        if (!result.exists) {
          console.log(t('common.usuariodocumentonotfoundredirectingtosignup'));
          await auth.signOut();
          showToast({
            type: "info",
            message: t("auth.userNotFoundPleaseSignUp"),
          });
          setLoading(false); // Desactivar loading ANTES de navigate
          setTimeout(() => {
            navigate("/signup", {
              replace: true,
              state: { notice: t("auth.userNotFoundMessage") },
            });
          }, 100);
          return;
        }

        // Verificar si el usuario necesita onboarding
        const userData = result.data;
        userDoc = userData; // Guardar para Family Mode check
        const hasFamilies = checkUserHasFamily(userData);
        
        if (!hasFamilies) {
          setLoading(false);
          sessionStorage.setItem("redirecting_to_onboarding", "true");
          navigate("/onboarding/welcome", { replace: true });
          return;
        }

              } catch (err) {
        console.error(t('common.errorcheckinguser'), err);
      }

      // Family Mode exit flow (email login)
      const params = new URLSearchParams(location.search);
      const exitFamilyMode = params.get("exitFamilyMode") === "1";

      if (exitFamilyMode && userDoc) {
        const userData = userDoc;
        const role = userData?.role;
        const isOwner = userData?.isOwner === true || role === "owner";
        const isAdmin =
          userData?.isAdmin === true ||
          role === "admin" ||
          role === "superadmin";

        if (isOwner || isAdmin) {
          disable();
          showToast({ type: "success", message: t("auth.familyModeDisabled") });
          setLoading(false); // Desactivar loading ANTES de navigate
          setTimeout(() => navigate("/groups/dashboard", { replace: true }), 100);
          return;
        } else {
          showToast({
            type: "error",
            message: t("auth.adminRequiredToExitFamilyMode"),
          });
          setLoading(false); // Desactivar loading ANTES de navigate
          setTimeout(() => navigate("/groups/home-groups", { replace: true }), 100);
          return;
        }
      }

      setLoading(false); // Desactivar loading ANTES de navigate
      setTimeout(() => navigate("/", { replace: true }), 100);
    } catch (err: any) {
      const code = err?.code as string | undefined;
      switch (code) {
        case "auth/invalid-email":
          setError(t("validation.emailNotValid"));
          break;
        case "auth/user-not-found":
          setError(t("validation.userNotExists"));
          setLoading(false); // Desactivar loading ANTES de navigate
          setTimeout(() => {
            navigate("/signup?from=signin&reason=user-not-found", {
              replace: true,
              state: { notice: t("auth.userNotFoundMessage") },
            });
          }, 100);
          return;
        case "auth/wrong-password":
        case "auth/invalid-credential":
          setError(t("validation.wrongPassword"));
          break;
        case "auth/invalid-api-key":
          setError(t("validation.invalidApiKey"));
          break;
        default:
          setError(t("auth.signInFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const isNative = Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios';
  const isAppleSignInSupported = Capacitor.getPlatform() === 'ios' || !isNative;

  return (
    <>
      {loading && <GlobalLoader message={t("auth.signingIn", "Iniciando sesión...")} />}
      <div className="flex flex-col flex-1 w-full overflow-y-auto lg:w-1/2 no-scrollbar p-6 sm:p-10 justify-center" style={{ backgroundColor: '#fff5ebff' }}>
      {/* Botón volver solo en web */}
      {!isNative && (
        <div className="w-full max-w-md mx-auto mb-5">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <ChevronLeftIcon className="size-5" />
            {t("auth.goBack")}
          </Link>
        </div>
      )}

      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        <div className="mb-5 sm:mb-8">
          <h1 className="mb-2 font-black text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
            {t("auth.signIn")}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("auth.enterEmailAndPasswordToSignIn")}
          </p>
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          className="w-full inline-flex items-center justify-center gap-3 py-3 text-sm font-black text-gray-700 transition-all bg-white dark:bg-gray-800 rounded-2xl px-7 hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-gray-200 dark:border-gray-700 shadow-[0_4px_12px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.1)]"
        >
          {/* SVG de Google */}
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.7511 10.1944C18.7511 9.47495 18.6915 8.94995 18.5626 8.40552H10.1797V11.6527H15.1003C15.0011 12.4597 14.4654 13.675 13.2749 14.4916L13.2582 14.6003L15.9087 16.6126L16.0924 16.6305C17.7788 15.1041 18.7511 12.8583 18.7511 10.1944Z"
              fill="#4285F4"
            />
            <path
              d="M10.1788 18.75C12.5895 18.75 14.6133 17.9722 16.0915 16.6305L13.274 14.4916C12.5201 15.0068 11.5081 15.3666 10.1788 15.3666C7.81773 15.3666 5.81379 13.8402 5.09944 11.7305L4.99473 11.7392L2.23868 13.8295L2.20264 13.9277C3.67087 16.786 6.68674 18.75 10.1788 18.75Z"
              fill="#34A853"
            />
            <path
              d="M5.10014 11.7305C4.91165 11.186 4.80257 10.6027 4.80257 9.99992C4.80257 9.3971 4.91165 8.81379 5.09022 8.26935L5.08523 8.1534L2.29464 6.02954L2.20333 6.0721C1.5982 7.25823 1.25098 8.5902 1.25098 9.99992C1.25098 11.4096 1.5982 12.7415 2.20333 13.9277L5.10014 11.7305Z"
              fill="#FBBC05"
            />
            <path
              d="M10.1789 4.63331C11.8554 4.63331 12.9864 5.34303 13.6312 5.93612L16.1511 3.525C14.6035 2.11528 12.5895 1.25 10.1789 1.25C6.68676 1.25 3.67088 3.21387 2.20264 6.07218L5.08953 8.26943C5.81381 6.15972 7.81776 4.63331 10.1789 4.63331Z"
              fill="#EB4335"
            />
          </svg>
          {t("auth.signInWithGoogle")}
        </button>

        {isAppleSignInSupported && (
          <button
            type="button"
            onClick={handleAppleSignIn}
            className="mt-3 w-full inline-flex items-center justify-center gap-3 py-3 text-sm font-black text-gray-700 transition-all bg-white dark:bg-gray-800 rounded-2xl px-7 hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-gray-200 dark:border-gray-700 shadow-[0_4px_12px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.1)]"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.365 1.43c0 1.14-.415 2.204-1.225 3.13-.986 1.14-2.59 2.02-4.1 1.898-.196-1.366.45-2.74 1.35-3.77.986-1.14 2.7-1.97 3.975-2.02.125.255.0.51.0.762zm4.61 17.108c-.54 1.234-.792 1.78-1.485 2.87-.967 1.52-2.33 3.416-4.02 3.43-1.5.015-1.89-.97-3.93-.955-2.04.015-2.47.973-3.97.958-1.69-.015-2.98-1.74-3.95-3.26-2.72-4.26-3.01-9.25-1.33-11.85 1.19-1.85 3.06-2.94 4.82-2.94 1.79 0 2.92.98 4.4.98 1.44 0 2.32-.98 4.38-.98 1.57 0 3.23.85 4.41 2.32-3.89 2.1-3.26 7.55.655 9.425z" />
            </svg>
            {t('auth.signInWithApple', { defaultValue: 'Continuar con Apple' })}
          </button>
        )}

        <div className="relative py-3 sm:py-5">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200 dark:border-gray-800"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="p-2 text-gray-400 bg-white dark:bg-gray-900 sm:px-5 sm:py-2">
              {t("auth.or")}
            </span>
          </div>
        </div>

        <div className="p-6 rounded-[28px] shadow-[0_10px_30px_-5px_rgba(5,85,144,0.04)] bg-white dark:bg-gray-900">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
                {t("auth.email")} <span className="text-error-500">*</span>
              </label>
              <input
                placeholder="info@gmail.com"
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-[42px] px-4 rounded-full border-none bg-gray-100/50 dark:bg-gray-800/60 focus:ring-2 focus:ring-emerald-500/25 transition-all outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
                {t("auth.password")} <span className="text-error-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={t("forms.enterYourPassword")}
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-[42px] px-4 rounded-full border-none bg-gray-100/50 dark:bg-gray-800/60 focus:ring-2 focus:ring-emerald-500/25 transition-all outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                >
                  {showPassword ? (
                    <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                  ) : (
                    <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                  )}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 flex-wrap">
              <Checkbox
                checked={isChecked}
                onChange={setIsChecked}
                label={t("auth.keepMeLoggedIn")}
              />
              <Link
                to="/reset-password"
                className="text-sm font-semibold text-brand-500 hover:text-brand-600 dark:text-brand-400 whitespace-nowrap"
              >
                {t("auth.forgotPassword")}
              </Link>
            </div>

            {error && <div className="text-sm font-semibold text-error-500">{error}</div>}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 text-sm font-black text-white transition-all rounded-2xl shadow-[0_4px_12px_rgba(0,0,0,0.15)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.2)] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#055590ff' }}
              >
                {loading ? t("auth.signingIn") : t("auth.signIn")}
              </button>
            </div>
          </form>
        </div>

        <div className="mt-5">
          <p className="text-sm font-semibold text-center text-gray-700 dark:text-gray-400 sm:text-start">
            {t("auth.noAccount")}{" "}
            <Link
              to="/signup"
              className="font-black text-brand-500 hover:text-brand-600 dark:text-brand-400"
            >
              {t("auth.signUp")}
            </Link>
          </p>
        </div>
      </div>
      </div>
    </>
  );
}
